import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Plus, KeyRound, UserX, UserCheck, Trash2, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import UserAccessDialog from "@/pages/admin/UserAccessDialog";
import TeacherClassesPicker from "@/pages/admin/TeacherClassesPicker";
import { toast } from "sonner";
import DeletionRequests from "@/pages/admin/DeletionRequests";
import Attachments from "@/pages/directeur/Attachments";
import StatusBadge from "@/components/StatusBadge";

const ALL_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "directeur", label: "Directeur" },
  { value: "maitre", label: "Maître" },
];

const emptyForm = (isDirecteur) => ({
  username: "",
  password: "",
  name: "",
  role: isDirecteur ? "maitre" : "directeur",
  is_independent: false,
  classes: { organisation: "simple", niveaux: [], flux: "", lettre: "" },
});

export default function Users() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const isDirecteur = me?.role === "directeur";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm(isDirecteur));
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [accessUser, setAccessUser] = useState(null);

  const ROLES = isDirecteur ? ALL_ROLES.filter((r) => r.value === "maitre") : ALL_ROLES;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const body = {
        username: form.username,
        password: form.password,
        name: form.name,
        role: form.role,
      };
      if (isAdmin && form.role === "maitre") body.is_independent = !!form.is_independent;
      // For directeur creating a maître (or admin creating a non-independent maître) — attach class info
      const attachClass = form.role === "maitre" && (isDirecteur || (isAdmin && !form.is_independent));
      const c = form.classes;
      if (attachClass && c.niveaux.length) {
        if (c.organisation === "simple" && c.niveaux.length === 1) {
          body.class_niveau = c.niveaux[0];
          body.class_nom = c.lettre ? `${c.niveaux[0]} ${c.lettre}` : c.niveaux[0];
        } else {
          body.niveaux = c.niveaux;
          body.organisation = c.organisation;
          body.flux = c.flux || null;
        }
      }
      await api.post("/users", body);
      toast.success("Compte créé");
      setOpen(false);
      setForm(emptyForm(isDirecteur));
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { blocked: !u.blocked });
      toast.success(u.blocked ? "Compte débloqué" : "Compte bloqué");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const resetPw = async () => {
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { new_password: pwValue });
      toast.success("Mot de passe réinitialisé");
      setPwOpen(false); setPwValue(""); setPwUser(null);
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (u) => {
    if (!confirm(`Supprimer ${u.username} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
            {isDirecteur ? "Mes maîtres" : "Utilisateurs"}
          </h1>
          <p className="text-sm text-[#5C736A]">
            {isDirecteur
              ? "Créez les comptes de vos maîtres. Vous pourrez ensuite leur affecter des classes."
              : "Mêmes formulaires que l'inscription publique : école, classes et rattachement sont créés automatiquement."}
          </p>
        </div>
        {isAdmin && (
          <Link to="/app/users/new" data-testid="add-user-btn">
            <Button className="bg-[#1F7A63] hover:bg-[#165c4b] text-white"><Plus className="w-4 h-4 mr-2" /> Ajouter un utilisateur</Button>
          </Link>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          {isDirecteur && <DialogTrigger asChild>
            <Button className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="add-teacher-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau maître
            </Button>
          </DialogTrigger>}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isDirecteur ? "Créer un maître" : "Créer un compte"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nom complet</Label>
                <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Identifiant</Label>
                <Input data-testid="form-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </div>
              <div>
                <Label>Mot de passe</Label>
                <Input data-testid="form-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              {isAdmin && (
                <div>
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, is_independent: false })}>
                    <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isAdmin && form.role === "maitre" && (
                <div className="flex items-start gap-3 p-3 bg-[#E8F2EF] rounded-lg border border-[#1F7A63]/20">
                  <input
                    type="checkbox"
                    id="indep"
                    data-testid="form-independent"
                    checked={form.is_independent}
                    onChange={(e) => setForm({ ...form, is_independent: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#1F7A63]"
                  />
                  <label htmlFor="indep" className="text-sm cursor-pointer">
                    <span className="font-semibold">Maître indépendant</span>
                    <div className="text-xs text-[#5C736A] mt-0.5">
                      Cochez si ce maître n&apos;est lié à aucun directeur. Il configurera lui-même son école après connexion.
                    </div>
                  </label>
                </div>
              )}

              {form.role === "maitre" && (isDirecteur || (isAdmin && !form.is_independent)) && (
                <TeacherClassesPicker value={form.classes} onChange={(classes) => setForm({ ...form, classes })} />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} data-testid="form-submit" className="bg-[#1F7A63] hover:bg-[#165c4b] text-white">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isAdmin && <DeletionRequests onChanged={load} />}
      {isDirecteur && <Attachments />}

      <div className="ek-card p-0 overflow-hidden">
        <div className="ek-table-wrap">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Identifiant</TableHead>
                {isAdmin && <TableHead>Rôle</TableHead>}
                <TableHead>État</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={5}><div className="skel h-6" /></TableCell></TableRow>
              ))}
              {!loading && users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Badge className="bg-[#E8F2EF] text-[#1F7A63] hover:bg-[#E8F2EF]">
                        {u.role}{u.role === "maitre" && u.is_independent ? " · indép." : ""}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell>
                    <StatusBadge user={u} />
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setPwUser(u); setPwOpen(true); }}>
                          <KeyRound className="w-4 h-4 mr-2" /> Réinit. mot de passe
                        </DropdownMenuItem>
                        {isAdmin && u.role !== "admin" && (
                          <DropdownMenuItem onClick={() => setAccessUser(u)} data-testid={`user-access-${u.username}`}>
                            <Building2 className="w-4 h-4 mr-2" /> Rôle et écoles
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => toggleBlock(u)}>
                          {u.blocked ? <UserCheck className="w-4 h-4 mr-2" /> : <UserX className="w-4 h-4 mr-2" />}
                          {u.blocked ? "Réactiver" : "Suspendre"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => del(u)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-[#5C736A] py-10">
                    {isDirecteur ? "Aucun maître. Créez le premier !" : "Aucun utilisateur."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <UserAccessDialog target={accessUser} onClose={() => setAccessUser(null)} onSaved={load} />

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau mot de passe — {pwUser?.username}</DialogTitle></DialogHeader>
          <Input type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Nouveau mot de passe" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Annuler</Button>
            <Button onClick={resetPw} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="confirm-reset-pw">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
